import cx from 'classnames';
import React, { forwardRef, useCallback, useContext, useEffect, useRef, } from 'react';
import { Typography } from '../../foundations/typography';
import { scrollElementsIntoView } from '../../utils/scrollElementsIntoView';
import ExpansionPanel from '../ExpansionPanel';
import { AccordionContext } from './AccordionContext';
import { ChevronIcon } from './ChevronIcon';
import { PlusMinusIcon } from './PlusMinusIcon';
import styles from './Accordion.module.scss';
var AccordionItemButton = forwardRef(function (_a, ref) {
    var _b;
    var regionId = _a.regionId, isOpen = _a.isOpen, buttonClassName = _a.buttonClassName, titleId = _a.titleId, onClick = _a.onClick, userCanInteractWithButton = _a.userCanInteractWithButton, theme = _a.theme, headingElement = _a.headingElement, styleHeadingsAs = _a.styleHeadingsAs, title = _a.title;
    var titleElement = (React.createElement(Typography, { element: headingElement, styleAs: styleHeadingsAs, noMargins: true, className: styles.titleText }, title));
    if (!userCanInteractWithButton) {
        return (React.createElement("div", { ref: ref, className: cx(styles.fakeTitleButton, buttonClassName) }, titleElement));
    }
    return (React.createElement("button", { type: "button", "aria-controls": regionId, "aria-expanded": isOpen, className: cx(styles.titleButton, buttonClassName, (_b = {},
            _b[styles.border] = theme === 'chevronRight',
            _b)), id: titleId, onClick: onClick, ref: ref },
        theme === 'chevron' && React.createElement(ChevronIcon, { isOpen: isOpen }),
        titleElement,
        theme === 'chevronRight' && (React.createElement("div", { className: styles.chevronRight, "data-testid": "chevron-icon-right-wrapper" },
            React.createElement(ChevronIcon, { isOpen: isOpen }))),
        theme === 'plusMinus' && React.createElement(PlusMinusIcon, { isOpen: isOpen })));
});
/**
 * @tdr
 * @tdr-deprecated-props: theme
 * @tdr-deprecated-date: 2022-07-12
 */
export var AccordionItem = function (_a) {
    var _b;
    var _c = _a.defaultExpanded, defaultExpanded = _c === void 0 ? false : _c, theme = _a.theme, id = _a.id, headingElement = _a.headingElement, title = _a.title, children = _a.children, buttonClassName = _a.buttonClassName, contentClassName = _a.contentClassName;
    var context = useContext(AccordionContext);
    if (!context) {
        throw new Error('AccordionContext not found. You need to wrap the AccordionItem into an Accordion');
    }
    var contextTheme = context.theme, styleHeadingsAs = context.styleHeadingsAs, getIsOpen = context.getIsOpen, setIsOpen = context.setIsOpen, type = context.type, scrollToItemWhenOpened = context.scrollToItemWhenOpened;
    var firstRender = useRef(true);
    var isOpen = (defaultExpanded && firstRender.current) || getIsOpen(id);
    var userCanInteractWithButton = type !== 'alwaysOpenSingleItem' || !isOpen;
    var toggle = useCallback(function () { return setIsOpen(id); }, [id, setIsOpen]);
    var skipScroll = useRef(true);
    var buttonRef = useRef(null);
    var contentRef = useRef(null);
    useEffect(function () {
        if (scrollToItemWhenOpened &&
            !skipScroll.current &&
            isOpen &&
            buttonRef.current &&
            contentRef.current) {
            var topOffset = void 0;
            if (type !== 'default' && buttonRef.current.parentElement) {
                var accordionWrapper = buttonRef.current.parentElement;
                var contents = Array.from(accordionWrapper.querySelectorAll(".accordionItemBody"));
                var currentContentIndex = contents.indexOf(contentRef.current);
                var previousElements = contents.slice(0, currentContentIndex);
                var previousElementsHeight = previousElements.reduce(function (acc, value) { return acc + value.scrollHeight; }, 0);
                topOffset = -previousElementsHeight;
            }
            scrollElementsIntoView([buttonRef.current, contentRef.current], {
                topAdjustment: topOffset,
            });
        }
        skipScroll.current = false;
    }, [scrollToItemWhenOpened, isOpen, type]);
    // This useEffect runs twice when React strict mode is active in dev mode, hence the extra check
    // on the firstRender ref.
    // https://reactjs.org/docs/strict-mode.html#detecting-unexpected-side-effects
    useEffect(function () {
        if (firstRender.current && defaultExpanded) {
            skipScroll.current = true;
            toggle();
        }
        firstRender.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var usedTheme = theme || contextTheme;
    var titleId = "".concat(id, "-title");
    var regionId = "".concat(id, "-region");
    return (React.createElement(React.Fragment, null,
        React.createElement(AccordionItemButton, { regionId: regionId, isOpen: isOpen, buttonClassName: buttonClassName, titleId: titleId, onClick: toggle, userCanInteractWithButton: userCanInteractWithButton, theme: usedTheme, headingElement: headingElement, styleHeadingsAs: styleHeadingsAs, title: title, ref: buttonRef }),
        React.createElement(Typography, { element: ExpansionPanel, styleAs: "paragraph", isOpen: isOpen, id: regionId, role: "region", "aria-labelledby": titleId, className: cx(contentClassName, 'accordionItemBody', (_b = {},
                _b[styles.bodyChevron] = usedTheme === 'chevron',
                _b[styles.bodyPlusMinus] = usedTheme === 'plusMinus',
                _b)), "aria-hidden": !isOpen, ref: contentRef }, children),
        React.createElement("hr", { className: styles.line })));
};
//# sourceMappingURL=AccordionItem.js.map